'use client';

import { useCookieConsent } from '@/components/client/cookie-consent/cookie-consent-hooks';
import { Button } from '@noirproperties/ui/components/ui/button';
import { Switch } from '@noirproperties/ui/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@noirproperties/ui/components/ui/dialog';
import { Trans } from '@lingui/react/macro';
import { useState, useEffect, useRef, useMemo } from 'react';
import type { ConsentCategories } from '@/lib/consent/gtag';

type CookieSettingsModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export default function CookieSettingsModal({
  open,
  onOpenChange,
}: CookieSettingsModalProps) {
  const { consentCategories, setConsentCategories } = useCookieConsent();

  const [localCategories, setLocalCategories] = useState<ConsentCategories>({
    functionality: 'granted',
    analytics: 'denied',
    ads: 'denied',
  });
  const hasInitialized = useRef(false);

  // Initialize form with current consent state
  useEffect(() => {
    if (consentCategories && !hasInitialized.current) {
      setLocalCategories(consentCategories);
      hasInitialized.current = true;
    }
  }, [consentCategories]);

  // Check if there are unsaved changes
  const isDirty = useMemo(() => {
    // If no consent categories exist yet (first-time visitor),
    // compare against default "denied" state
    const currentCategories = consentCategories || {
      functionality: 'granted',
      analytics: 'denied',
      ads: 'denied',
    };

    return (
      localCategories.analytics !== currentCategories.analytics ||
      localCategories.ads !== currentCategories.ads
    );
  }, [localCategories, consentCategories]);

  const handleSave = () => {
    // Ensure functionality is always granted
    const categoriesToSave = {
      ...localCategories,
      functionality: 'granted' as const,
    };

    setConsentCategories(categoriesToSave);
    onOpenChange(false); // Close modal after saving
  };

  const handleCancel = () => {
    // Reset to current saved state
    if (consentCategories) {
      setLocalCategories(consentCategories);
    } else {
      setLocalCategories({
        functionality: 'granted',
        analytics: 'denied',
        ads: 'denied',
      });
    }
    onOpenChange(false);
  };

  const handleAnalyticsChange = (checked: boolean) => {
    setLocalCategories((prev) => ({
      ...prev,
      analytics: checked ? 'granted' : 'denied',
    }));
  };

  const handleAdsChange = (checked: boolean) => {
    setLocalCategories((prev) => ({
      ...prev,
      ads: checked ? 'granted' : 'denied',
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] lg:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>
            <Trans>Cookie Settings</Trans>
          </DialogTitle>
          <DialogDescription>
            <Trans>
              Manage your cookie preferences. You can enable or disable
              different types of cookies below.
            </Trans>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Functionality cookies - always enabled */}
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-4 sm:gap-6">
              <div className="min-w-0 flex-1 space-y-0.5">
                <div className="text-base font-medium">
                  <Trans>Essential Cookies</Trans>
                </div>
                <div className="text-sm text-muted-foreground">
                  <Trans>
                    These cookies are necessary for the website to function and
                    cannot be switched off.
                  </Trans>
                </div>
              </div>
              <div className="flex-shrink-0">
                <Switch
                  checked={true}
                  disabled
                  className="data-[state=checked]:border-gray-900 data-[state=checked]:bg-gray-900"
                />
              </div>
            </div>
          </div>

          {/* Analytics cookies */}
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-4 sm:gap-6">
              <div className="min-w-0 flex-1 space-y-0.5">
                <div className="text-base font-medium">
                  <Trans>Analytics Cookies</Trans>
                </div>
                <div className="text-sm text-muted-foreground">
                  <Trans>
                    These cookies help us understand how visitors interact with
                    our website.
                  </Trans>
                </div>
              </div>
              <div className="flex-shrink-0">
                <Switch
                  checked={localCategories.analytics === 'granted'}
                  onCheckedChange={handleAnalyticsChange}
                  className="data-[state=checked]:border-gray-900 data-[state=checked]:bg-gray-900"
                />
              </div>
            </div>
          </div>

          {/* Marketing/Ads cookies */}
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-4 sm:gap-6">
              <div className="min-w-0 flex-1 space-y-0.5">
                <div className="text-base font-medium">
                  <Trans>Marketing Cookies</Trans>
                </div>
                <div className="text-sm text-muted-foreground">
                  <Trans>
                    These cookies are used to track visitors across websites for
                    marketing purposes.
                  </Trans>
                </div>
              </div>
              <div className="flex-shrink-0">
                <Switch
                  checked={localCategories.ads === 'granted'}
                  onCheckedChange={handleAdsChange}
                  className="data-[state=checked]:border-gray-900 data-[state=checked]:bg-gray-900"
                />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleCancel}
            className="border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            <Trans context="close a dialog popup">Cancel</Trans>
          </Button>
          <Button
            onClick={handleSave}
            disabled={!isDirty}
            className="bg-gray-900 text-white hover:bg-gray-800 focus:ring-gray-900"
          >
            <Trans>Save preferences</Trans>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
